import { useEffect, useMemo, useState } from 'react';
import { ACTIVE_STATUS, FUTURE_STATUS } from 'domain/shared/assignment';
import useEmployeeGroupService from 'domain/shared/assignment/group/shared/employeeGroupService';
import { useTechnologyService } from 'domain/shared/career';
import { useEmployeeSuggestionsService, usePositionService } from 'domain/shared/employee';
import { useProjectService } from 'domain/shared/project';
import { useServiceService } from 'domain/shared/service';
import type { ResourceData } from 'framework/crud';
import useFeatureToggle from 'framework/featureToggle';
import { ASC } from 'framework/list';
import type { ListTableSort } from 'framework/ui/ui-builder';
import {
  type FilterBarFilterConfig,
  type FilterBarFilterOption,
  type FilterBarFiltersProps,
  type FilterBarQuickFilter,
  type FilterState,
} from 'framework/ViewTypes/components';
import { FilterCondition } from 'framework/types';
import { Features } from 'infrastructure/featureToggle';

interface UseAssignmentsFilterBarParams {
  filters: FilterState;
  setFilters: (filters: FilterState) => void;
  resetFilters: () => void;
  setSort: (sort: ListTableSort) => void;
}

type NamedItem = { id: StringOrNumber; name: string };
type EmployeeSuggestion = { alias: string; nameEn?: { firstName: string; lastName: string } };
type ProjectSuggestion = NamedItem & { projectName?: string };
type SuggestionOptions = Pick<
  Extract<FilterBarFilterConfig, { type: 'autosuggestion' }>,
  'loadOptions' | 'resolveOption'
>;

interface AssignmentsFilterDictionaries {
  positions: FilterBarFilterOption[];
  technologies: FilterBarFilterOption[];
  groups: FilterBarFilterOption[];
}

const EMPTY_DICTIONARIES: AssignmentsFilterDictionaries = { positions: [], technologies: [], groups: [] };

const YES = 'YES';

const BOOLEAN_OPTIONS: FilterBarFilterOption[] = [
  { value: YES, label: 'Yes' },
  { value: 'NO', label: 'No' },
];

const STATUS_OPTIONS: FilterBarFilterOption[] = [
  { value: ACTIVE_STATUS, label: 'Active' },
  { value: FUTURE_STATUS, label: 'Future' },
];

const QUICK_FILTERS: (FilterBarQuickFilter & { sort: ListTableSort })[] = [
  {
    id: 'active',
    label: 'All active assignments',
    state: { status: { in: [ACTIVE_STATUS] } },
    sort: { columnId: 'to', direction: ASC },
  },
  {
    id: 'future',
    label: 'All future assignments',
    state: { status: { in: [FUTURE_STATUS] } },
    sort: { columnId: 'from', direction: ASC },
  },
  {
    id: 'bench',
    label: 'All bench',
    state: { bench: { eq: YES } },
    sort: { columnId: 'to', direction: ASC },
  },
];

const mapNamedOption = (item: NamedItem): FilterBarFilterOption => ({
  value: String(item.id),
  label: item.name,
});

const mapProjectOption = (item: ProjectSuggestion): FilterBarFilterOption => ({
  value: String(item.id),
  label: item.projectName ?? item.name,
});

const mapEmployeeOption = (employee: EmployeeSuggestion): FilterBarFilterOption => ({
  value: employee.alias,
  label: [employee.alias, employee.nameEn?.firstName, employee.nameEn?.lastName].filter(Boolean).join(' '),
});

const loadDictionary = async (load: () => Promise<NamedItem[]>): Promise<FilterBarFilterOption[]> => {
  try {
    return (await load()).map(mapNamedOption);
  } catch {
    return [];
  }
};

const createSuggestionOptions = <Item>(
  search: (request: ResourceData) => Promise<{ result?: Item[] }>,
  mapOption: (item: Item) => FilterBarFilterOption,
  { searchSource = 'name', idSource = 'id' } = {},
): SuggestionOptions => ({
  loadOptions: async value => {
    const response = await search({ filter: { [searchSource]: { ct: value } } });
    return (response.result ?? []).map(mapOption);
  },
  resolveOption: async value => {
    const response = await search({ filter: { [idSource]: { eq: value } } });
    const item = response.result?.[0];
    return item ? mapOption(item) : null;
  },
});

const dropdownFilter = (
  source: string,
  label: string,
  options: FilterBarFilterOption[],
  { multiple = true } = {},
): FilterBarFilterConfig => ({
  id: source,
  source,
  label,
  type: 'dropdown',
  condition: multiple ? FilterCondition.IN : FilterCondition.EQ,
  options,
  multiple,
});

const booleanFilter = (source: string, label: string) =>
  dropdownFilter(source, label, BOOLEAN_OPTIONS, { multiple: false });

const suggestionFilter = (
  source: string,
  label: string,
  options: SuggestionOptions,
  { minSearchLength = 0 } = {},
): FilterBarFilterConfig => ({
  id: source,
  source,
  label,
  type: 'autosuggestion',
  condition: FilterCondition.EQ,
  minSearchLength,
  ...options,
});

const employeeFilter = (source: string, label: string, options: SuggestionOptions) =>
  suggestionFilter(source, label, options, { minSearchLength: 1 });

const dateRangeFilter = (source: string, label: string): FilterBarFilterConfig => ({
  id: source,
  source,
  label,
  type: 'dateRange',
});

const useAssignmentsFilterBar = ({
  filters,
  setFilters,
  resetFilters,
  setSort,
}: UseAssignmentsFilterBarParams): FilterBarFiltersProps => {
  const { isFeatureEnabled } = useFeatureToggle();
  const isGroupAssignmentsEnabled = isFeatureEnabled(Features.GROUP_ASSIGNMENTS);
  const { findAll: findPositions } = usePositionService();
  const { findAll: findTechnologies } = useTechnologyService();
  const { getAll: findGroups } = useEmployeeGroupService();
  const { searchWithContractors } = useEmployeeSuggestionsService();
  const { searchSuggestions: searchProjects } = useProjectService();
  const { searchSuggestions: searchServices } = useServiceService();
  const [dictionaries, setDictionaries] = useState<AssignmentsFilterDictionaries>(EMPTY_DICTIONARIES);

  useEffect(() => {
    let isActive = true;

    Promise.all([
      loadDictionary(findPositions),
      loadDictionary(findTechnologies),
      isGroupAssignmentsEnabled ? loadDictionary(findGroups) : Promise.resolve([]),
    ]).then(([positions, technologies, groups]) => {
      if (isActive) setDictionaries({ positions, technologies, groups });
    });

    return () => {
      isActive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGroupAssignmentsEnabled]);

  const suggestions = useMemo(
    () => ({
      employees: createSuggestionOptions(searchWithContractors, mapEmployeeOption, {
        searchSource: 'searchString',
        idSource: 'alias',
      }),
      projects: createSuggestionOptions(searchProjects, mapProjectOption),
      services: createSuggestionOptions(searchServices, mapNamedOption),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return {
    value: filters,
    onChange: setFilters,
    onReset: resetFilters,
    quickFilters: QUICK_FILTERS,
    onQuickFilterSelect: ({ id }) => {
      const quickFilter = QUICK_FILTERS.find(item => item.id === id);
      if (quickFilter) setSort(quickFilter.sort);
    },
    filters: [
      booleanFilter('bench', 'Is bench'),
      booleanFilter('isVirtual', 'Is virtual'),
      booleanFilter('isPending', 'Is pending'),
      dropdownFilter('status', 'Status', STATUS_OPTIONS),
      employeeFilter('owner.alias', 'Owner', suggestions.employees),
      employeeFilter('specification:individualAssignmentsByEmployeeAlias', 'Worker', suggestions.employees),
      employeeFilter('specification:backupForEmployeeAlias', 'Backup for worker', suggestions.employees),
      ...(isGroupAssignmentsEnabled
        ? [dropdownFilter('specification:groupAssignmentsByGroupId', 'Public service', dictionaries.groups)]
        : []),
      employeeFilter('createdBy.alias', 'Created by', suggestions.employees),
      dropdownFilter('specification:assignmentsByPositionIds', 'Position', dictionaries.positions),
      dropdownFilter('technology.id', 'Technology', dictionaries.technologies),
      suggestionFilter('project.id', 'Project', suggestions.projects),
      suggestionFilter('serviceId', 'Service', suggestions.services),
      dateRangeFilter('from', 'Start date'),
      dateRangeFilter('to', 'End date'),
    ],
  };
};

export default useAssignmentsFilterBar;
